// ---------------------------------------------------------------------------
// socketService.ts — Socket.IO client singleton
// ---------------------------------------------------------------------------
import { io, Socket } from "socket.io-client";
import { cookieUtils } from "../utils/cookies";

// Derive the WS URL from the env. Strip trailing `/api` if present so
// socket.io connects to the root namespace on the same host.
const WS_URL =
    import.meta.env.VITE_WS_URL ||
    (import.meta.env.VITE_API_BASE_URL || "http://localhost:3000")
        .replace(/\/api\/?$/, "");

if (import.meta.env.DEV) {
    console.log("[socket] WS_URL resolved to:", WS_URL);
}

let socket: Socket | null = null;

/**
 * Get (or create) the shared socket instance.
 * The connection is lazily initialised on the first call.
 */
export function getSocket(): Socket {
    if (!socket) {
        socket = io(WS_URL, {
            transports: ["websocket"],
            autoConnect: true,
            auth: (cb: (data: object) => void) => {
                cb({ token: cookieUtils.getAccessToken() || "" });
            },
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
        });

        if (import.meta.env.DEV) {
            socket.on("connect", () => {
                console.log("[socket] ✅ connected, id:", socket?.id);
                console.log("[socket] transport:", socket?.io?.engine?.transport?.name);
            });
            socket.on("disconnect", (reason) =>
                console.warn("[socket] 🔌 disconnected:", reason),
            );
            socket.on("connect_error", (err) => {
                console.error("[socket] ❌ connect_error:", err.message, err);
                const token = cookieUtils.getAccessToken();
                console.log("[socket] auth token present?", !!token, "| token preview:", token?.slice(0, 20));
            });
            socket.on("reconnect_attempt", (n) =>
                console.log("[socket] 🔁 reconnect attempt #", n),
            );
            socket.on("reconnect_error", (err) =>
                console.warn("[socket] reconnect_error:", err.message),
            );
            socket.on("reconnect_failed", () =>
                console.error("[socket] ❌ reconnect_failed — giving up"),
            );
        }
    }

    return socket;
}

/**
 * Disconnect and destroy the socket instance.
 * Call this on app-level logout or teardown.
 */
export function disconnectSocket(): void {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
}
